import React, { useState, useEffect, useCallback } from "react";

import { useTranslation } from "react-i18next";
import { useLoading } from "../../context/LoadingContext";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";

import { IoMdArrowBack } from "react-icons/io";
import { IoClose } from "react-icons/io5";
import { ChevronRight } from "lucide-react";
import { RiInformation2Fill } from "react-icons/ri";

import config from "../../config";
import FloatingMenu from "../FloatingMenu";
import ErrorBanner from "../ui/ErrorBanner";
import TransactionStatus from "./TransactionStatus";

import {
    formatCurrency,
    sanitizeNumberInput,
    preventInvalidKeyInput
} from "../../utils/global";

import {
    fetchDepositPromotionList,
    fetchPaymentGatewayList,
    fetchPaymentGatewayStatus,
    sendInvoice
} from "../../utils/api";
import { assetPath } from "../../utils/assets";

const HIDDEN_PAYMENT_GATEWAY_IDS = [];
const HIDDEN_PAYMENT_GATEWAY_NAMES = ["Payment Gateway"];

const hiddenGatewayIds = new Set(HIDDEN_PAYMENT_GATEWAY_IDS.map(String));
const hiddenGatewayNames = new Set(HIDDEN_PAYMENT_GATEWAY_NAMES.map((name) => name.toLowerCase()));

const PaymentGatewayDeposit = () => {
    const { t } = useTranslation();
    const { setLoading } = useLoading();
    const { token, phoneNumber } = useAuth();
    const navigate = useNavigate();

    const [error, setError] = useState({
        message: "",
        shake: false
    });
    const [modal, setModal] = useState({
        show: false,
        url: null,
    });
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [deposit, setDeposit] = useState({
        amount: "",
        display: "",
        options: ["10000", "20000", "50000", "100000", "200000", "500000"]
    });
    const [promotion, setPromotion] = useState({
        list: [],
        selected: null,
        confirmed: null
    });
    const [payment, setPayment] = useState({
        selected: null,
        options: []
    });
    const [timer, setTimer] = useState({
        started: false,
        timeout: true,
        secondsLeft: null,
        lastApiParams: null
    });

    const clearError = useCallback(() => {
        setError(prev => ({ ...prev, message: "" }));
    }, []);

    const generateInvoiceUniqueId = useCallback(() => {
        const timestamp = Date.now().toString(36);
        const randomNum = Math.random().toString(36).substr(2, 5);
        return timestamp + randomNum;
    }, []);

    const getInvoiceSourceId = useCallback(() => {
        const playerName = String(phoneNumber || "player").replace(/[^a-zA-Z0-9]/g, "");
        return `${playerName}-${generateInvoiceUniqueId()}`;
    }, [phoneNumber, generateInvoiceUniqueId]);

    const getPaymentOptionMeta = useCallback((gateway) => {
        switch (gateway.channel_name) {
            case "QRpay":
                return {
                    paymentMethod: "QR",
                    bankCode: "DANAQR",
                    icon: assetPath("/icons/QRIS.png"),
                };
            case "QRis":
                return {
                    paymentMethod: "QR",
                    bankCode: "QRIS",
                    icon: assetPath("/icons/QRIS.png"),
                };
            default:
                return {
                    paymentMethod: "BANK",
                    bankCode: gateway.payment_name || gateway.name,
                    icon: assetPath("/icons/card.png"),
                };
        }
    }, []);

    const checkMinMaxDeposit = useCallback((amount) => {
        if (isNaN(amount)) {
            clearError();
            return;
        }

        const minDeposit = 10000;
        const maxDeposit = 10000000;
        let message = "";

        if (amount < minDeposit) {
            message = t("minDeposit", { currency: config.CURRENCY_ISO_CODE, amount: formatCurrency(minDeposit, "currency", config.CURRENCY_ISO_CODE) });
        } else if (amount > maxDeposit) {
            message = t("maxDeposit", { currency: config.CURRENCY_ISO_CODE, amount: formatCurrency(maxDeposit, "currency", config.CURRENCY_ISO_CODE) });
        }

        setError(prev => prev.message === message ? prev : { ...prev, message, shake: !!message });

        if (message) {
            setPayment(prev => ({ ...prev, selected: null }));
            setPromotion(prev => ({ ...prev, selected: null, confirmed: null }));
            setTimeout(() => setError(prev => ({ ...prev, shake: false })), 500);
        }
    }, [t, clearError]);

    const handleDepositAmountChange = useCallback((input) => {
        const inputValue = input?.target ? input.target.value : input;
        const sanitizedValue = sanitizeNumberInput(String(inputValue ?? ""));

        if (!sanitizedValue) {
            setDeposit(prev => ({ ...prev, amount: "", display: "" }));
            clearError();
            return;
        }

        setDeposit(prev => ({
            ...prev,
            amount: sanitizedValue,
            display: formatCurrency(sanitizedValue, "currency", config.CURRENCY_ISO_CODE),
        }));

        checkMinMaxDeposit(Number(sanitizedValue));
    }, [checkMinMaxDeposit, clearError]);

    const loadData = useCallback(async () => {
        setLoading(true);
        clearError();

        try {
            const [promotionResponse, paymentGatewayResponse] = await Promise.all([
                fetchDepositPromotionList(),
                fetchPaymentGatewayList(),
            ]);
            const activePaymentGateways = paymentGatewayResponse.success && Array.isArray(paymentGatewayResponse.data)
                ? paymentGatewayResponse.data.filter((gateway) => {
                    const gatewayNames = [
                        gateway.name,
                        gateway.payment_name,
                        gateway.channel_name,
                    ].filter(Boolean).map((name) => String(name).toLowerCase());

                    return Number(gateway.status) === 1
                        && !hiddenGatewayIds.has(String(gateway.id))
                        && !gatewayNames.some((name) => hiddenGatewayNames.has(name));
                })
                : [];
            const paymentOptions = activePaymentGateways.map((gateway) => ({
                ...gateway,
                ...getPaymentOptionMeta(gateway),
                name: gateway.payment_name || gateway.name,
                paymentGatewayId: gateway.id,
            }));

            setPromotion(prev => ({
                ...prev,
                list: promotionResponse.success && Array.isArray(promotionResponse.data) ? promotionResponse.data : []
            }));
            setPayment(prev => ({
                ...prev,
                selected: paymentOptions.some((option) => option.id === prev.selected?.id) ? prev.selected : null,
                options: paymentOptions,
            }));
        } catch (error) {
            setError(prev => ({ ...prev, message: t("pleaseTryLater") }));
        } finally {
            setLoading(false);
        }
    }, [t, setLoading, clearError, getPaymentOptionMeta]);

    const handleApi = useCallback(async (params) => {
        setLoading(true);
        clearError();
        setPaymentStatus(null);

        try {
            const response = await sendInvoice(params);
            if (response.success) {
                const url = response?.data;
                if (url) {
                    setTimer(prev => ({
                        ...prev,
                        lastApiParams: params,
                        secondsLeft: 360,
                        started: true,
                        timeout: false,
                    }));
                    setPaymentStatus("pending");
                    setModal({ show: true, url });
                    return;
                }

                setError(prev => ({ ...prev, message: t("linkNotAvailable") }));
            } else {
                setError({ message: response.message || response.data || t("pleaseTryLater"), shake: true });
                setTimeout(() => setError(prev => ({ ...prev, shake: false })), 500);
            }
        } catch (err) {
            setError(prev => ({ ...prev, message: t("failedToFetchLink") }));
        } finally {
            setLoading(false);
        }
    }, [t, setLoading, clearError]);

    const handleSubmit = useCallback(() => {
        const currentParams = {
            sourceId: getInvoiceSourceId(),
            paymentMethod: payment.selected.paymentMethod,
            bankCode: payment.selected.bankCode,
            paymentGatewayId: payment.selected.paymentGatewayId,
            amount: deposit.amount,
            mcode: config.PAYMENT_GATEWAY_MCODE,
            promotion: promotion.confirmed,
            type: "deposit",
        };

        const timerIsOut = timer.timeout;
        const amountChanged = timer.lastApiParams?.amount !== currentParams.amount;
        const promotionChanged = timer.lastApiParams?.promotion?.id !== currentParams.promotion?.id;

        if (timerIsOut || amountChanged || promotionChanged || !timer.lastApiParams) {
            handleApi(currentParams);
        } else {
            setModal(prev => ({ ...prev, show: true }));
        }
    }, [handleApi, deposit.amount, payment.selected, promotion.confirmed, timer.timeout, timer.lastApiParams, getInvoiceSourceId]);

    useEffect(() => {
        if (error.message) {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [error.message]);

    useEffect(() => {
        if (token) {
            loadData();
        }
    }, [token, loadData]);

    useEffect(() => {
        if (!timer.started || timer.secondsLeft <= 0) return;

        const id = setInterval(() => {
            setTimer(prev => {
                if (prev.secondsLeft <= 1) {
                    return { ...prev, secondsLeft: 0, timeout: true, started: false };
                }
                return { ...prev, secondsLeft: prev.secondsLeft - 1 };
            });
        }, 1000);

        return () => clearInterval(id);
    }, [timer.started, timer.secondsLeft]);

    useEffect(() => {
        const sourceId = timer.lastApiParams?.sourceId;

        if (!modal.show || !sourceId || paymentStatus !== "pending" || timer.timeout) {
            return;
        }

        let isMounted = true;
        let isChecking = false;

        const normalizeStatus = (status) => String(status || "").toLowerCase();
        const isSuccessStatus = (status) => ["success", "completed", "approved", "processed"].includes(status);
        const isFailedStatus = (status) => ["failed", "fail", "rejected", "error"].includes(status);
        const isExpiredStatus = (status) => ["expired", "timeout"].includes(status);

        const checkStatus = async () => {
            if (isChecking) return;
            isChecking = true;

            try {
                const response = await fetchPaymentGatewayStatus(sourceId);
                const status = normalizeStatus(response?.data?.status);

                if (!isMounted || !response?.success) {
                    return;
                }

                if (isSuccessStatus(status)) {
                    setPaymentStatus("success");
                    setModal(prev => ({ ...prev, url: null }));
                    setTimer(prev => ({ ...prev, timeout: true, started: false, secondsLeft: 0 }));
                    window.dispatchEvent(new Event("wallet:refresh"));
                    window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred?.("success");
                } else if (isFailedStatus(status)) {
                    setPaymentStatus("failed");
                    setModal(prev => ({ ...prev, url: null }));
                    setTimer(prev => ({ ...prev, timeout: true, started: false, secondsLeft: 0 }));
                    window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred?.("error");
                } else if (isExpiredStatus(status)) {
                    setPaymentStatus("expired");
                    setModal(prev => ({ ...prev, url: null }));
                    setTimer(prev => ({ ...prev, timeout: true, started: false, secondsLeft: 0 }));
                    window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred?.("warning");
                }
            } catch (error) {
                // Keep the QR available if a transient status check fails.
            } finally {
                isChecking = false;
            }
        };

        checkStatus();
        const intervalId = setInterval(checkStatus, 4000);

        return () => {
            isMounted = false;
            clearInterval(intervalId);
        };
    }, [modal.show, paymentStatus, timer.lastApiParams, timer.timeout]);

    return (
        <section className="page-shell">
            {!modal.show && (
                <div className="page-header">
                    <div
                        className="cursor-pointer bg-secondary container-back-button text-color-blue rounded-full"
                        onClick={() => navigate("/deposit")}
                    >
                        <IoMdArrowBack className="size-sm" />
                        <span className="text-sm">
                            {t("backToLobby")}
                        </span>
                    </div>
                    <button className="cursor-pointer" onClick={() => navigate("/")}>
                        <IoClose className="size-lg text-color-blue" />
                    </button>
                </div>
            )}

            <ErrorBanner message={error.message} />

            <div className="px-4 pt-[30px] pb-[100px]">
                <p className="mb-3 text-sm text-center font-bold text-white">
                    {t("enterDepositAmount")}
                </p>

                <div className="info-banner mb-8">
                    <RiInformation2Fill className="size-[30px] text-border-yellow" />
                    <div className="flex flex-col flex-grow">
                        <span className="text-xs text-white">
                            {t("minDeposit", { currency: config.CURRENCY_ISO_CODE, amount: formatCurrency(3000, "currency", config.CURRENCY_ISO_CODE) })}
                        </span>
                        <span className="text-xs text-white">
                            {t("maxDeposit", { currency: config.CURRENCY_ISO_CODE, amount: formatCurrency(10000000, "currency", config.CURRENCY_ISO_CODE) })}
                        </span>
                    </div>
                </div>

                <div className="max-w-md my-6 text-center mx-auto w-fit">
                    <div
                        className={`text-[36px] font-bold text-white transition-transform duration-300 ${
                            error.shake ? "animate-shake" : ""
                        }`}
                    >
                        <span className="text-white/70 pl-1">
                            {config.CURRENCY_ISO_CODE}
                        </span>

                        <input
                            type="text"
                            value={deposit.display || ""}
                            onKeyDown={preventInvalidKeyInput}
                            onChange={handleDepositAmountChange}
                            placeholder="0"
                            className="bg-transparent text-white text-[36px] font-bold text-right outline-none pr-2 w-auto min-w-[2ch] max-w-[10ch] md:max-w-[20ch]"
                            style={{ width: `${(deposit.amount?.length || 1) + 1}ch` }}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-3 my-8">
                    {deposit.options.map((amount, index) => (
                        <div
                            key={index}
                            className="relative"
                        >
                            {amount === "50000" && (
                                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-2 py-1 text-xxs font-bold text-black uppercase rounded-full btn-gradient w-max">
                                    {t("popular")}
                                </div>
                            )}
                            <div
                                onClick={() => {
                                    handleDepositAmountChange(amount);
                                }}
                                className={`cursor-pointer p-2 h-[36px] text-center transition-colors rounded-sm ${amount === `${deposit.amount}` ? "btn-gradient text-black" : "bg-main-gradient text-white"}`}
                            >
                                <span className="text-sm font-bold">
                                    {formatCurrency(amount, "currency", config.CURRENCY_ISO_CODE)}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>

                <div
                    className={`transition-all duration-300 ease-in-out ${
                        !error.message && deposit.amount
                            ? "opacity-100 translate-y-0 scale-100"
                            : "opacity-0 translate-y-4 scale-95 pointer-events-none"
                    }`}
                >
                    <div className="mb-8">
                        <p className="mb-3 text-sm font-bold text-white">
                            {t("selectPaymentChannel")}
                        </p>

                        <div className="rounded-lg bg-option-blue">
                            {payment.options.map((option) => (
                                <div
                                    key={option.id}
                                    className={`container-xs justify-start gap-2 cursor-pointer text-white 
                                        border-b border-option-selected-blue first:rounded-t-lg last:rounded-b-lg last:border-b-0
                                        ${payment.selected?.id === option.id ? "bg-option-second-selected-blue" : ""}`}
                                    onClick={() => {
                                        setPayment(prev => ({ ...prev, selected: prev.selected?.id === option.id ? null : option }));
                                        setPromotion(prev => ({ ...prev, selected: null, confirmed: null }));
                                        clearError();
                                    }}
                                >
                                    <img
                                        src={option.icon}
                                        alt={option.name}
                                        className="size-xl object-contain"
                                    />
                                    <div className="flex flex-col">
                                        <span className="text-sm">
                                            {option.name}
                                        </span>
                                        <span className="text-xs text-color-blue">
                                            {option.bankCode}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div
                    className={`transition-all duration-300 ease-in-out ${
                        !error.message && payment.selected
                            ? "opacity-100 translate-y-0 scale-100"
                            : "opacity-0 translate-y-4 scale-95 pointer-events-none"
                    }`}
                >
                    <div className="mb-8">
                        <p className="mb-3 text-sm font-bold text-white">
                            {t("selectPromotion")}
                        </p>

                        {promotion.list.length > 0 && (
                            <div className="overflow-hidden">
                                {promotion.list.map((promo) => (
                                    <div key={promo.id}>
                                        <div
                                            className={`container-promotion-button rounded-sm bg-dropdown-bg flex-between ${promotion.selected?.id === promo.id ? "rounded-b-none" : "mb-3"}`}
                                            onClick={() => {
                                                setPromotion(prev => {
                                                    const newSelection = prev.selected?.id === promo.id ? null : promo;
                                                    return { ...prev, selected: newSelection, confirmed: null };
                                                });
                                                clearError();
                                            }}
                                        >
                                            <div className="flex-center gap-3">
                                                <img
                                                    src={assetPath("/icons/gift.png")}
                                                    alt="Gift"
                                                    className="size-md"
                                                />
                                                <span className="text-sm text-white">
                                                    {promo.name}
                                                </span>
                                            </div>
                                            <div className="flex-center gap-3">
                                                <ChevronRight
                                                    className={`size-sm text-white animate-rotate ${promotion.selected?.id === promo.id ? "rotate-[90deg]" : ""}`}
                                                />
                                            </div>
                                        </div>

                                        {promotion.selected?.id === promo.id && (
                                            <div className="flex flex-col p-4 mb-3 bg-main-gradient animate-fadeIn last:rounded-b-sm">
                                                <div className="flex flex-col p-3 mb-3 bg-secondary rounded-lg">
                                                    <span className="text-sm text-white">
                                                        <span className="font-bold">{t("bonusAward")} </span>
                                                        : {promotion.selected.bonus_type === 1 ? promotion.selected.percentage + "%" : `${config.CURRENCY_ISO_CODE} ${formatCurrency(promotion.selected.amount, "currency", config.CURRENCY_ISO_CODE)}`}
                                                    </span>
                                                    <br />

                                                    <span className="text-sm text-white">
                                                        <span className="font-bold">{t("turnOver")} </span>
                                                        : x{promotion.selected.turnover_multiply}
                                                    </span>
                                                    <br />

                                                    <span className="text-sm text-white">
                                                        <span className="font-bold">{t("minimumDeposit")} </span>
                                                        : {config.CURRENCY_ISO_CODE} {formatCurrency(promotion.selected.min_deposit, "currency", config.CURRENCY_ISO_CODE)}
                                                    </span>
                                                    <br />

                                                    <span className="text-sm text-white">
                                                        <span className="font-bold">{t("maximumBonus")} </span>
                                                        : {promotion.selected.bonus_type === 1
                                                        ? `${config.CURRENCY_ISO_CODE} ${formatCurrency(promotion.selected.max_bonus_limit, "currency", config.CURRENCY_ISO_CODE)}`
                                                        : "-"}
                                                    </span>
                                                    <br />

                                                    <span className="text-sm text-white">
                                                        {t("promotionTNC")}
                                                    </span>
                                                </div>

                                                <button
                                                    className={`container-sm rounded-sm ${promotion.confirmed && promotion.confirmed !== "skip" && promotion.confirmed?.id === promo.id ? "bg-promotion-selected" : "btn-gradient"}`}
                                                    onClick={() => {
                                                        setPromotion(prev => {
                                                            if (!prev.selected) return prev;
                                                            const isSame = prev.confirmed && prev.confirmed.id === prev.selected.id;
                                                            return { ...prev, confirmed: isSame ? null : prev.selected };
                                                        });
                                                        clearError();
                                                    }}
                                                >
                                                    <span
                                                        className={`text-sm text-center font-bold ${promotion.confirmed && promotion.confirmed !== "skip" && promotion.confirmed?.id === promo.id ? "text-white" : ""}`}
                                                    >
                                                        {promotion.confirmed && promotion.confirmed !== "skip" && promotion.confirmed?.id === promo.id ? t("promotionSelected") : t("selectThisPromotion")}
                                                    </span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        <div
                            className={`container-promotion-button rounded-sm bg-dropdown-bg transition-all duration-300 ease-in-out flex-between 
                                ${promotion.selected === "skip" ? "container-promotion-button-border bg-secondary border-2 border-dropdown-bg" : ""}
                            `}
                            onClick={() => {
                                setPromotion(prev => ({
                                    ...prev,
                                    selected: prev.selected === "skip" ? null : "skip",
                                    confirmed: prev.confirmed === "skip" ? null : "skip"
                                }));
                                clearError();
                            }}
                        >
                            <div className="flex-center gap-3">
                                <img
                                    src={assetPath("/icons/skip_promo.png")}
                                    alt="skip"
                                    className="size-md"
                                />
                                <span
                                    className={`text-sm text-white ${promotion.selected === "skip" ? "font-bold" : ""}`}
                                >
                                    {t("skipPromotion")}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <button
                    className={`fixed bottom-[60px] md:bottom-0 left-0 right-0 container-lg h-[60px] text-center btn-gradient transition-all duration-300 ease-in-out ${
                        !error.message && deposit.amount && payment.selected && promotion.confirmed
                            ? "opacity-100 translate-y-0 scale-100"
                            : "opacity-0 translate-y-4 scale-95 pointer-events-none"
                    }`}
                    onClick={() => {
                        handleSubmit();
                    }}
                >
                    <span className="text-sm font-bold">
                        {t("submit")}
                    </span>
                </button>
            </div>

            {modal.show && (
                <div className="modal-overlay lg:top-[162px]">
                    <div className="sticky top-0 z-200 flex-between bg-fourth px-4 py-5 lg:hidden">
                        <div
                            className="cursor-pointer bg-secondary container-back-button text-color-blue rounded-full"
                            onClick={() => {
                                setModal(prev => ({ ...prev, show: false }));
                            }}
                        >
                            <IoMdArrowBack className="size-sm" />
                            <span className="text-sm">
                                {t("backToLobby")}
                            </span>
                        </div>
                        <button className="cursor-pointer" onClick={() => navigate("/")}>
                            <IoClose className="size-lg text-color-blue" />
                        </button>
                    </div>

                    {paymentStatus === "success" && (
                        <section className="min-h-[calc(100vh-68px)] bg-secondary lg:min-h-[calc(100vh-162px)]">
                            <TransactionStatus
                                status="success"
                                type="deposit"
                                title="transactionSuccessTitle"
                                message="transactionSuccessMessage"
                                amount={formatCurrency(timer.lastApiParams?.amount || deposit.amount, "currency", config.CURRENCY_ISO_CODE)}
                                onPrimaryAction={() => navigate("/")}
                                primaryTitle={t("startPlaying")}
                                onSecondaryAction={() => navigate("/wallet")}
                                secondaryTitle={t("wallet")}
                            />
                        </section>
                    )}

                    {paymentStatus === "failed" && (
                        <section className="min-h-[calc(100vh-68px)] bg-secondary lg:min-h-[calc(100vh-162px)]">
                            <TransactionStatus
                                status="unsuccess"
                                type="deposit"
                                title="transactionFailedTitle"
                                message="transactionFailedMessage"
                                onPrimaryAction={() => {
                                    setModal({ show: false, url: null });
                                    setPaymentStatus(null);
                                }}
                                primaryTitle={t("tryAgain")}
                                onSecondaryAction={() => navigate("/")}
                                secondaryTitle={t("backToHome")}
                            />
                        </section>
                    )}

                    {!["success", "failed"].includes(paymentStatus) && !timer.timeout && (
                        <iframe
                            src={modal.url}
                            title={t("externalPayment")}
                            className="w-full h-[calc(100vh-68px)] border-none lg:h-[calc(100vh-162px)]"
                        />
                    )}

                    {!["success", "failed"].includes(paymentStatus) && timer.timeout && (
                        <section className="min-h-[calc(100vh-68px)] bg-secondary lg:min-h-[calc(100vh-162px)]">
                            <TransactionStatus
                                status="warning"
                                type="deposit"
                                title="timerEnded"
                                message="paymentQrCodeExpired"
                            />
                        </section>
                    )}
                </div>
            )}

            <FloatingMenu />
        </section>
    );
};

export default PaymentGatewayDeposit;
